import React, {
  useRef,
  useEffect,
  useCallback,
  ReactNode,
  MutableRefObject,
  ComponentProps,
  ReactElement,
  Ref,
} from 'react';

import PropTypes from 'prop-types';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
} from 'react-virtualized';

import { useForwardedRef } from '@adsk/alloy-react-helpers';

type VirtualizedListProps<T extends Record<string, unknown>> = Omit<
  ComponentProps<typeof List>,
  'rowRenderer' | 'width' | 'height' | 'rowHeight' | 'rowCount'
> & {
  overscanCount?: number;
  items?: T[];
  renderItem?: ({ item, index }: { item: T; index: number }) => ReactNode;
  onRowsRendered?: ({
    cellMesureCacheRef,
    items,
    startIndex,
    stopIndex,
  }: {
    cellMesureCacheRef: MutableRefObject<CellMeasurerCache>;
    items: T[];
    startIndex: number;
    stopIndex: number;
  }) => void;
};

/**
 * A Virtualized List component, that allows generic scrolling functionality for large lists.
 */
const VirtualizedListWrapped = <T extends Record<string, unknown>>(
  {
    overscanCount,
    items = [],
    renderItem,
    onRowsRendered,
    ...props
  }: VirtualizedListProps<T>,
  ref: Ref<List>
) => {
  const cellMesureCacheRef = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 10,
    })
  );

  const listRef = useForwardedRef(ref);

  // Wrapper for row rendering. takes care of cell-measurement and applying the style needed for react-virtualized.
  const rowRenderer = useCallback(
    ({ index, key, parent, style }) => {
      return (
        <CellMeasurer
          cache={cellMesureCacheRef.current}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          {({ registerChild }) => (
            <div
              ref={registerChild as (element: HTMLDivElement) => void}
              style={style}
            >
              {renderItem?.({ item: items[index], index })}
            </div>
          )}
        </CellMeasurer>
      );
    },
    [items, renderItem]
  );

  // A hack suggested by Dan Abramov, in order to do a deep compare when using useEffect:
  // https://github.com/facebook/react/issues/14476#issuecomment-471199055
  const itemsString = JSON.stringify(items);

  // Equivalent to componentWillReceiveProps
  useEffect(() => {
    cellMesureCacheRef.current.clearAll();
    if (listRef.current) {
      listRef.current.forceUpdateGrid();
      listRef.current.recomputeRowHeights();
    }
  }, [itemsString, listRef]);

  return (
    <AutoSizer>
      {({ height, width }) => (
        <List
          ref={listRef}
          style={{ outline: 'none' }} // Needed to remove the default blue outline of the list.
          rowRenderer={rowRenderer}
          overscanRowCount={overscanCount}
          deferredMeasurementCache={cellMesureCacheRef.current}
          width={width}
          height={height}
          rowHeight={cellMesureCacheRef.current.rowHeight}
          rowCount={items.length}
          items={items}
          onRowsRendered={({ startIndex, stopIndex }) => {
            if (onRowsRendered) {
              onRowsRendered({
                cellMesureCacheRef,
                items,
                startIndex,
                stopIndex,
              });
            }
          }}
          {...props}
        />
      )}
    </AutoSizer>
  );
};

const VirtualizedList = React.forwardRef(VirtualizedListWrapped);

VirtualizedList.displayName = 'VirtualizedList';

VirtualizedList.propTypes = {
  /**  Items array. Every item should be an object with only metadata - not a ReactNode! */
  items: PropTypes.array,
  /**  Item renderer - a callback with the following signature: ({ item, index }) */
  renderItem: PropTypes.func.isRequired,
  /**  Number of rows to render above/below the visible bounds of the list */
  overscanCount: PropTypes.number,
  /** Optional callback that's being called after the rows are being rendered. Signature: { cellMesureCacheRef, items, startIndex, stopIndex } */
  onRowsRendered: PropTypes.func,
};

export default VirtualizedList as <T extends Record<string, unknown>>(
  p: VirtualizedListProps<T>
) => ReactElement;
